import React, { useEffect, useState } from 'react';
import styles from './reviews.module.css';

export interface ReviewCommentContext {
  scenarioTitle: string;
  scenarioId: string;
  sceneId: string;
  missionLabel?: string;
  sceneExcerpt?: string;
}

interface ReviewCommentModalProps {
  isOpen: boolean;
  context: ReviewCommentContext;
  onClose: () => void;
  onSubmit: (body: string) => Promise<void>;
}

export const ReviewCommentModal: React.FC<ReviewCommentModalProps> = ({
  isOpen,
  context,
  onClose,
  onSubmit,
}) => {
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setBody('');
    setError(null);
    setSubmitting(false);
  }, [isOpen, context.sceneId]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, submitting]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const trimmed = body.trim();
    if (trimmed.length < 3) {
      setError('Le commentaire doit contenir au moins 3 caractères.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec de l’envoi');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={submitting ? undefined : onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className={styles.modalHeader}>
          <h2>Commentaire</h2>
          <button type="button" className={styles.closeBtn} onClick={onClose} disabled={submitting} aria-label="Fermer">
            ×
          </button>
        </header>

        <div className={styles.modalBody}>
          <div className={styles.contextBlock}>
            <div>
              <strong>{context.scenarioTitle}</strong>
            </div>
            {context.missionLabel && <div>Mission : {context.missionLabel}</div>}
            {context.sceneExcerpt && (
              <div className={styles.excerpt}>({context.sceneExcerpt})</div>
            )}
          </div>

          <textarea
            className={styles.textarea}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Décrivez le problème ou votre retour sur cette scène…"
            maxLength={2000}
            disabled={submitting}
            autoFocus
          />

          {error && <p className={styles.error}>{error}</p>}
        </div>

        <footer className={styles.modalFooter}>
          <button type="button" className={styles.btnSecondary} onClick={onClose} disabled={submitting}>
            Annuler
          </button>
          <button type="button" className={styles.btnPrimary} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Envoi…' : 'Envoyer'}
          </button>
        </footer>
      </div>
    </div>
  );
};
